import { Component,ParentComponent, createResource, createSignal, ErrorBoundary, JSXElement, Show, Suspense } from "solid-js";
import { Icon } from "../../explorer/src/components/Icon";
import { BuildOrder } from "./BuildOrder";
import { SCORE_TABLE_GROUPS } from "./config";
import { getGameSummary } from "./parsing/parseReplay";
import { ScoreTable } from "./ScoreTable";
import { Timeline, TimelineSkeleton } from "./Timeline";

const smallButtonCSSClass = `text-gray-300 font-bold text-sm bg-gray-600 rounded px-3 py-2 hover:bg-gray-500 hover:text-gray-50 transition`;
const containerCssClass = `bg-gray-700 sm:rounded-2xl container my-10  max-w-screen-2xl mx-auto`;

const Container: ParentComponent<{ title: string; icon: string; controls?: JSXElement }> = (props) => {
  return (
    <div class={containerCssClass}>
      <div class="flex items-center px-8 border-gray-500 border-b">
        <h3 class="font-bold text-xl py-6 flex-auto ">
          <Icon icon={props.icon} class=" text-xl mr-4 " />
          {props.title}
        </h3>
        {props.controls}
      </div>
      {props.children}
    </div>
  );
};

export const Replay: Component<{ url: string }> = (props) => {
  const [data] = createResource(props.url, getGameSummary);
  const [wrap, setWrap] = createSignal(false);
  const [grouped, setGrouped] = createSignal(true);

  return (
    <ErrorBoundary
      fallback={(e, reset) => {
        console.error(e);
        return (
          <Container title="Timeline" icon="chart-line">
            <div class="grid place-items-center text-gray-200 w-full h-96">
              <div class="text-center space-y-3">
                <Icon icon="triangle-exclamation" class="text-3xl text-gray-400" />
                <h2 class="text-xl text-gray-100">Something went wrong</h2>
                <pre class="text-lg">{e.message}</pre>
                <button onClick={() => reset()} class={smallButtonCSSClass}>
                  <Icon icon="rotate-right" class="mr-2" /> Retry
                </button>
              </div>
            </div>
          </Container>
        );
      }}
    >
      <Container title="Timeline" icon="chart-line">
        <Suspense fallback={<TimelineSkeleton />}>
          <div>{data() && <Timeline players={data()} />}</div>
        </Suspense>
      </Container>

      <Container title="Comparison" icon="table-columns">
        <Suspense fallback={<div class="grid place-items-center text-gray-200 w-full h-96">Loading...</div>}>
          <div class="overflow-x-auto w-full">{data() && <ScoreTable players={data()} groups={SCORE_TABLE_GROUPS} />}</div>
        </Suspense>
      </Container>

      <Container
        title="Build Order"
        icon="rectangle-history"
        controls={
          <div class="flex items-center gap-3">
            {wrap() ? (
              <button onClick={() => setWrap(false)} class={smallButtonCSSClass}>
                <Icon icon="arrows-from-line" /> Expand
              </button>
            ) : (
              <button onClick={() => setWrap(true)} class={smallButtonCSSClass}>
                <Icon icon="arrows-to-line" /> Expand
              </button>
            )}

            {grouped() ? (
              <button onClick={() => setGrouped(false)} class={smallButtonCSSClass}>
                <Icon icon="diagram-cells" /> Unstack
              </button>
            ) : (
              <button onClick={() => setGrouped(true)} class={smallButtonCSSClass}>
                <Icon icon="diagram-previous" /> Stack
              </button>
            )}
          </div>
        }
      >
        <Suspense fallback={<div class="grid place-items-center text-gray-200 w-full h-96">Loading...</div>}>
          <BuildOrder summaries={data()} wrap={wrap()} group={grouped()} />
        </Suspense>
      </Container>

      <Show when={data()?.flatMap((g) => g.issues)}>
        {(issues) => {
          const uniqueIssues = [...new Set(issues())].sort();
          return issues()?.length ? (
            <Container title="Parsing Issues" icon="triangle-exclamation">
              <pre class="p-8">
                <details>
                  <summary>Encountered {uniqueIssues.length} issues</summary>
                  <ul class="list-disc list-inside space-y-1 mt-2">
                    {uniqueIssues.map((issue) => (
                      <li>{issue}</li>
                    ))}
                  </ul>
                </details>
              </pre>
              <div class="px-8 pb-8 text-gray-100">
                <p class="my-4">Help AoE4World by sharing the above list on our Discord.</p>
                <a class={smallButtonCSSClass} href="https://discord.gg/YCTx8HEecv" target="_blank">
                  Report on Discord
                </a>
              </div>
            </Container>
          ) : (
            <></>
          );
        }}
      </Show>
    </ErrorBoundary>
  );
};
